import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { outreachAdmin } from '@discovr/api';
import { campaignKeys, refreshOutreachKpisNow } from '@discovr/features';
import { entryActionErrorCopy } from './sendLabels';

/**
 * Suppress posts the row's current address; unsuppress deletes the row's
 * suppression by id (POST /admin/outreach/suppressions, DELETE …/{id},
 * outreach:manage) — ruling 52, no new BE surface. The BE's
 * AddSuppressionInput.Reason is a closed enum (manual/hard_bounce/complaint/
 * unsubscribe — INVALID_REASON on anything else), so the confirm step's
 * free-text "Why?" note is for the operator's own record and isn't sent —
 * the same pattern Settings → Suppressions' own remove dialog uses.
 *
 * Shared by the Inbox and the CRM's creator list.
 */
export function useSuppressionMutations(campaignId: string) {
  const queryClient = useQueryClient();
  // A suppression moves the creator's stage, so every view that counts creators refreshes, not just the rows. The
  // KPIs are re-read past Go's cache: a plain invalidate would get the pre-suppression view back.
  const refreshCreatorViews = () =>
    Promise.all([
      ...[
        campaignKeys.outreachRowsAll(campaignId),
        campaignKeys.outreachCounts(campaignId),
        campaignKeys.outreachRatesAll(campaignId),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      refreshOutreachKpisNow(queryClient, campaignId),
    ]);

  const suppress = useMutation({
    mutationFn: (address: string) => outreachAdmin.addSuppression({ address, reason: 'manual', campaign_id: campaignId }),
    onSuccess: refreshCreatorViews,
    onError: (e: unknown) => toast.error(entryActionErrorCopy(e)),
  });
  const unsuppress = useMutation({
    // By id, never by address: the address must not reach a URL (E2E M7).
    mutationFn: (suppressionId: number) => outreachAdmin.removeSuppression(suppressionId),
    onSuccess: refreshCreatorViews,
    onError: (e: unknown) => toast.error(entryActionErrorCopy(e)),
  });

  return { suppress, unsuppress };
}
